import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDataProvider, type RaRecord } from "react-admin";
import debounce from "lodash.debounce";
import { useWatch } from "react-hook-form";

import { planItsAutoLookupEffect, runItsAutoLookupSearch } from "./its-auto-lookup-search";
import { ITS_AUTO_DEBOUNCE_MS, isCompleteItsId, normalizeItsQuery } from "./its-lookup-helpers";

/** Stable default — avoid `filter = {}` in params (new object every render → effect churn). */
export const NO_EXTRA_ITS_FILTER: Record<string, unknown> = {};

export type UseItsAutoLookupOptions = {
  source: string;
  filter?: Record<string, unknown>;
  debounceMs?: number;
  onFound: (record: RaRecord, its: string) => void;
  onNotFound?: (its: string) => void;
  onError?: (error: unknown, its: string) => void;
  /** Fired when the watched value is not a complete ITS id (including empty). */
  onIncomplete?: (its: string) => void;
};

export type UseItsAutoLookupResult = {
  loading: boolean;
  currentValue: string;
};

export function useItsAutoLookup({
  source,
  filter = NO_EXTRA_ITS_FILTER,
  debounceMs = ITS_AUTO_DEBOUNCE_MS,
  onFound,
  onNotFound,
  onError,
  onIncomplete,
}: UseItsAutoLookupOptions): UseItsAutoLookupResult {
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState(false);
  const lastQueryRef = useRef("");
  const watchedValRef = useRef("");
  const currentVal = useWatch({ name: source });
  const normalized = normalizeItsQuery(currentVal);
  watchedValRef.current = normalized;

  const onFoundRef = useRef(onFound);
  const onNotFoundRef = useRef(onNotFound);
  const onErrorRef = useRef(onError);
  const onIncompleteRef = useRef(onIncomplete);
  onFoundRef.current = onFound;
  onNotFoundRef.current = onNotFound;
  onErrorRef.current = onError;
  onIncompleteRef.current = onIncomplete;

  const runSearch = useCallback(
    (q: unknown) => {
      const t = normalizeItsQuery(q);
      // Mirror skip gates before loading so brief flashes do not occur on no-ops.
      if (!isCompleteItsId(t)) return;
      if (lastQueryRef.current === t) return;

      setLoading(true);
      void runItsAutoLookupSearch({
        q: t,
        getWatchedValue: () => watchedValRef.current,
        getLastQuery: () => lastQueryRef.current,
        setLastQuery: (query) => {
          lastQueryRef.current = query;
        },
        getList: (resource, params) => dataProvider.getList(resource, params),
        filter,
        onFound: (record, its) => onFoundRef.current(record, its),
        onNotFound: (its) => onNotFoundRef.current?.(its),
        onError: (err, its) => onErrorRef.current?.(err, its),
      }).finally(() => setLoading(false));
    },
    [dataProvider, filter]
  );

  const debouncedSearch = useMemo(
    () => debounce((q: unknown) => runSearch(q), debounceMs),
    [runSearch, debounceMs]
  );

  useEffect(() => {
    const plan = planItsAutoLookupEffect(normalized);
    if (plan.kind === "incomplete") {
      lastQueryRef.current = "";
      debouncedSearch.cancel();
      onIncompleteRef.current?.(plan.value);
      return undefined;
    }
    debouncedSearch(plan.value);
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch, normalized]);

  return { loading, currentValue: normalized };
}
